import { useValidatedCubeQuery } from "app/hooks/cube";
import { createCombinedError } from "app/hooks/cube/utils";

import { getCostFieldName, hasAccountFilters } from "./filterTransformUtils";
import { useCostReportingConfig } from "./useCostReportingConfig";
import { costStatsRawDataSchema } from "./useCostStats.schema";

import type { Query, Filter, ResultSet } from "@cubejs-client/core";
import type { CloudCostType } from "app/contexts/UserContext";
import type { FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";
import type { Range } from "types/cube";

interface UseCostStatsParams {
  dashboardKey: string;
  dateRange: Range;
  sectionKey?: string;
  /** Filter bar filters, built for the CSOTag cube when `isResourceFiltering` */
  filters?: FilterBarCubeFilters;
  /** Whether to query the CSOTag cube (resource tag filter or tag tab) */
  isResourceFiltering?: boolean;
  /** Action bar resource tag key */
  tag?: string;
  enabled?: boolean;
}

interface ChartDataPoint {
  x: string;
  y: number;
}

interface ChartSeries {
  id: string;
  data: ChartDataPoint[];
}

interface UseCostStatsResult {
  monthlySavingOpportunities: ChartSeries[];
  nonCompliantResourceCounts: ChartSeries[];
  isLoading: boolean;
  error: Error | null;
}

function buildQuery(
  cube: "CSO" | "CSOTag",
  dashboardKey: string,
  dateRange: Range,
  costType: CloudCostType | undefined,
  sectionKey?: string,
  filters?: FilterBarCubeFilters,
  tag?: string,
): Query {
  const costFieldName = getCostFieldName(costType, cube);
  const isByTag = cube === "CSOTag";

  return {
    measures: [costFieldName, `${cube}.nonCompliantResourceCount`],
    // Filtered queries only match a pre-aggregation when the filtered
    // dimensions are selected too; formatResults sums the series back up.
    dimensions: [
      `${cube}.dashboard_key`,
      ...(sectionKey ? [`${cube}.section_key`] : []),
      ...(isByTag
        ? [`${cube}.resource_tag_key`, `${cube}.resource_tag_value`]
        : []),
      ...(hasAccountFilters(filters)
        ? ["Account.key", "Account.name", "Account.email", "Account.groups"]
        : []),
    ],
    segments: sectionKey ? [] : [`${cube}.summary`],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: "day",
        dateRange,
      },
    ],
    filters: [
      {
        member: `${cube}.dashboard_key`,
        operator: "equals" as const,
        values: [dashboardKey],
      },
      ...(sectionKey
        ? [
            {
              member: `${cube}.section_key`,
              operator: "equals" as const,
              values: [sectionKey],
            } as Filter,
          ]
        : []),
      ...(isByTag && tag
        ? [
            {
              member: `${cube}.resource_tag_key`,
              operator: "equals" as const,
              values: [tag],
            } as Filter,
          ]
        : []),
      ...(filters ?? []),
    ],
    order: {
      [`${cube}.date`]: "desc",
    },
  };
}

/**
 * Sums a measure's series into one chart series. Selecting dimensions splits
 * each measure into a series per account or tag, keyed `<dims>,<measure>`.
 */
function formatResults(
  measure: string,
  resultSet: ResultSet | null,
): ChartSeries[] {
  const series = (resultSet?.series() ?? []).filter(
    (s) => s.key === measure || s.key.endsWith(`,${measure}`),
  );

  if (series.length === 0) {
    return [];
  }

  const totals = new Map<string, number>();
  series.forEach((s) => {
    s.series.forEach(({ x, value }) => {
      totals.set(x, (totals.get(x) ?? 0) + Number(value));
    });
  });

  return [
    {
      id: measure,
      data: Array.from(totals, ([x, y]) => ({ x, y })),
    },
  ];
}

export function useCostStats({
  dashboardKey,
  dateRange,
  sectionKey,
  filters,
  isResourceFiltering = false,
  tag,
  enabled = true,
}: UseCostStatsParams): UseCostStatsResult {
  const cube = isResourceFiltering ? "CSOTag" : "CSO";
  const costType = useCostReportingConfig();
  const costFieldName = getCostFieldName(costType, cube);

  const { resultSet, isLoading, error, validationError } =
    useValidatedCubeQuery(
      buildQuery(
        cube,
        dashboardKey,
        dateRange,
        costType,
        sectionKey,
        filters,
        tag,
      ),
      costStatsRawDataSchema(cube),
      { skip: !enabled },
    );

  const combinedError = createCombinedError(
    { error, validationError },
    { errorPrefix: "Stats query validation failed" },
  );

  const monthlySavingOpportunities = formatResults(costFieldName, resultSet);

  const nonCompliantResourceCounts = formatResults(
    `${cube}.nonCompliantResourceCount`,
    resultSet,
  );

  return {
    monthlySavingOpportunities,
    nonCompliantResourceCounts,
    isLoading,
    error: combinedError,
  };
}
